import { cx } from "class-variance-authority";
import { useCallback, useEffect, useRef, useState } from "react";
import { ComboBox, type ComboBoxProps, Input, type Key, ListBox, ListBoxItem, Popover } from "react-aria-components";
import { useTranslation } from "react-i18next";
import useMeasure from "react-use-measure";
import { useDebounceValue } from "usehooks-ts";

import { ArrowDropDownIcon } from "@/assets/icons/general/ArrowDropDown";
import { InlineIconButton } from "@/components/ui/buttons/InlineIconButton/InlineIconButton";
import {
  AutocompleteListItem,
  autocompleteListItemStyles,
} from "@/components/ui/inputs/Autocomplete/AutocompleteListItem";
import { InputHeader } from "@/components/ui/inputs/shared/InputHeader";
import { type TextInputVariantProps, textInput } from "@/components/ui/inputs/TextInput/TextInputWrapper";
import { Loader } from "@/components/ui/status/Loader/Loader";

const NEW_ITEM_ID = "new-item-id";
const NEW_ITEM_MIN_LENGTH = 3;
const THRESHOLD_FOR_FETCHING_NEXT_PAGE = 150;

export type AutocompleteItem<TKey extends Key = Key> = {
  id: TKey;
  label: string;
};

type AutocompleteProps<T extends object, TKey extends Key = Key> = Omit<ComboBoxProps<T>, "children"> &
  TextInputVariantProps & {
    label: string;
    items: AutocompleteItem<TKey>[];
    onCreateNewOption?: (value: string) => void;
    resetInputAfterSelection?: boolean;
    isNewItem?: boolean;
    hasNextPage?: boolean;
    fetchNextPage?: () => void;
    isFetchingNextPage?: boolean;
    onSearchChange?: (value: string) => void;
    isLoading?: boolean;
    showNewItemOption?: boolean;
    newItemTypeLabel?: string;
    placeholder?: string;
    unselectOption?: boolean;
    inputClassname?: string;
    inputIconClassname?: string;
    hideLabels?: boolean;
    comboboxClassName?: string;
  };

export const Autocomplete = <T extends object, TKey extends Key = Key>({
  variant,
  label,
  items,
  onCreateNewOption,
  onSelectionChange,
  onInputChange,
  resetInputAfterSelection = false,
  hasNextPage,
  fetchNextPage,
  isFetchingNextPage,
  onSearchChange,
  isLoading,
  showNewItemOption,
  newItemTypeLabel,
  placeholder,
  isRequired,
  unselectOption,
  inputClassname,
  inputIconClassname,
  hideLabels,
  comboboxClassName,
  ...props
}: AutocompleteProps<T, TKey>) => {
  const { t } = useTranslation();
  const [triggerRef, { width }] = useMeasure();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [value, setValue] = useState(
    items.find((item) => item.id === props.selectedKey)?.label || props.inputValue || "",
  );
  const [isOpen, setIsOpen] = useState(false);
  const [hasTriggeredFetch, setHasTriggeredFetch] = useState(false);
  const [debouncedValue] = useDebounceValue(value, 500);

  const [selectedKey, setSelectedKey] = useState<Key | null>(props.selectedKey || null);

  const showNewItem =
    showNewItemOption &&
    value.length >= NEW_ITEM_MIN_LENGTH &&
    !items?.find((item) => item?.label?.toLowerCase() === value.trim().toLowerCase());

  const newItems: (AutocompleteItem & { isNewItem?: boolean })[] = [
    ...(showNewItem ? [{ id: NEW_ITEM_ID, label: value, isNewItem: true }] : []),
    ...items,
  ];

  useEffect(() => {
    setHasTriggeredFetch(false);
  }, [items.length, isOpen]);

  useEffect(() => {
    onSearchChange?.(debouncedValue);
    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedValue]);

  const inputChangeHandler = (val: string) => {
    setValue(val);
    onInputChange?.(val);
  };

  const selectionChangeHandler = (id: Key | null) => {
    setIsOpen(false);
    if (id === selectedKey && unselectOption) {
      setSelectedKey(null);
      setValue("");
      onSelectionChange?.(null);
      return;
    }
    if (id === NEW_ITEM_ID) {
      onCreateNewOption?.(value);
    } else {
      onSelectionChange?.(id);
      setValue(items.find((item) => item.id === id)?.label || "");
    }

    if (resetInputAfterSelection) {
      setValue("");
      setSelectedKey(id);
      setTimeout(() => {
        setSelectedKey(null);
      }, 100);
    } else {
      setSelectedKey(id);
    }
  };

  const onScroll = useCallback(() => {
    if (!hasNextPage || !fetchNextPage || !scrollContainerRef.current || isFetchingNextPage || hasTriggeredFetch)
      return;

    const { scrollTop, clientHeight, scrollHeight } = scrollContainerRef.current;
    const isNearBottom = scrollTop + clientHeight >= scrollHeight - THRESHOLD_FOR_FETCHING_NEXT_PAGE;

    if (isNearBottom) {
      setHasTriggeredFetch(true);
      fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage, isFetchingNextPage, hasTriggeredFetch]);

  return (
    <ComboBox
      {...props}
      onInputChange={inputChangeHandler}
      onSelectionChange={selectionChangeHandler}
      selectedKey={selectedKey || props.selectedKey}
      inputValue={value}
      allowsEmptyCollection
      onOpenChange={setIsOpen}
      onFocusChange={setIsOpen}
      onBlur={() => {
        setIsOpen(false);
      }}
      className={comboboxClassName}
    >
      <InputHeader
        label={label}
        required={isRequired}
        hideLabels={hideLabels}
      />
      <div className="relative">
        <Input
          className={textInput({ variant, className: cx("pr-8", inputClassname) })}
          ref={triggerRef}
          value={value}
          onClick={() => {
            setIsOpen(true);
          }}
          placeholder={placeholder}
        />
        <div className="-translate-y-1/2 absolute top-1/2 right-input-side-spacing flex items-center">
          <InlineIconButton
            label=""
            icon={ArrowDropDownIcon}
            iconClassName={inputIconClassname}
          />
        </div>
      </div>

      <Popover
        className="my-4 overflow-hidden outline-none"
        style={{ width }}
        offset={0}
        isOpen={isOpen}
        onOpenChange={setIsOpen}
      >
        <div
          ref={scrollContainerRef}
          className="h-auto max-h-[300px] overflow-hidden overflow-y-auto rounded-s border border-elevation-outline-1 bg-elevation-surface-1 shadow-5 outline-none"
          onScroll={onScroll}
        >
          <ListBox className="outline-none">
            {isLoading && (
              <ListBoxItem
                className={cx(
                  autocompleteListItemStyles.item({ isNewItem: false }),
                  "flex flex-row items-center gap-2",
                )}
              >
                <Loader className="h-4 w-4" />
                <span>{t("shared.loading")}</span>
              </ListBoxItem>
            )}
            {newItems.map((item, index) => (
              <AutocompleteListItem
                key={item.id}
                index={index}
                item={item}
                newItemTypeLabel={newItemTypeLabel}
                isNewItem={item.isNewItem}
              />
            ))}
            {isFetchingNextPage && (
              <ListBoxItem
                className={cx(
                  autocompleteListItemStyles.item({ isNewItem: false }),
                  "flex flex-row items-center gap-2",
                )}
              >
                <Loader className="h-4 w-4" />
                <span>{t("shared.loading")}</span>
              </ListBoxItem>
            )}
          </ListBox>
        </div>
      </Popover>
    </ComboBox>
  );
};
